import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { DirectionProvider, Toaster, TooltipProvider } from '@/design-system';
import { createQueryClient } from '@/shared/api';
import { i18next } from '@/shared/i18n';
import { PreferencesProvider } from '@/shared/preferences';

import { createAppRouter } from './router';

function useDir(): 'ltr' | 'rtl' {
  return useSyncExternalStore(
    (cb) => {
      i18next.on('languageChanged', cb);
      return () => i18next.off('languageChanged', cb);
    },
    () => (document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'),
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(() => createAppRouter(queryClient));
  const dir = useDir();
  // Server-generated labels (next actions, document names, report titles) come in the request's language:
  // refetch them and re-run route loaders (breadcrumbs) after a language switch.
  useEffect(() => {
    const onChange = () => {
      void queryClient.invalidateQueries();
      void router.invalidate();
    };
    i18next.on('languageChanged', onChange);
    return () => i18next.off('languageChanged', onChange);
  }, [queryClient, router]);
  return (
    <QueryClientProvider client={queryClient}>
      <DirectionProvider dir={dir}>
        <TooltipProvider delayDuration={350}>
          <PreferencesProvider>
            <RouterProvider router={router} />
            <Toaster dir={dir} />
          </PreferencesProvider>
        </TooltipProvider>
      </DirectionProvider>
    </QueryClientProvider>
  );
}
